import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, TrendingUp, Users, Target, ArrowUp, CreditCard,
  Activity, Shield, UserCheck, AlertCircle, ChevronRight,
  Clock, Award, ArrowLeft
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

const ScribeUpAnalysisV2 = ({ onSectionChange }) => {
  const navigate = useNavigate();
  const [selectedView, setSelectedView] = useState('overview');
  
  // Format currency helper
  const formatCurrency = (value) => {
    if (typeof value === 'string') {
      // Remove any existing formatting
      value = value.replace(/[$,]/g, '');
    }
    const num = parseFloat(value);
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // VISA Credit Card Cohort Data - From CSV
  const visaCohortData = [
    { month: 'Oct-24', cohortSize: 108, beforeSpend: 2067, afterSpend: 2380, change: 313, pctChange: 15.1, retention: 'N/A' },
    { month: 'Nov-24', cohortSize: 470, beforeSpend: 1906, afterSpend: 2007, change: 101, pctChange: 5.3, retention: 'N/A' },
    { month: 'Dec-24', cohortSize: 482, beforeSpend: 1759, afterSpend: 1721, change: -38, pctChange: -2.2, retention: 'N/A' },
    { month: 'Jan-25', cohortSize: 493, beforeSpend: 1644, afterSpend: 1815, change: 172, pctChange: 10.5, retention: 'N/A' },
    { month: 'Feb-25', cohortSize: 260, beforeSpend: 2048, afterSpend: 2199, change: 151, pctChange: 7.4, retention: 'N/A' },
    { month: 'Mar-25', cohortSize: 631, beforeSpend: 1544, afterSpend: 1704, change: 160, pctChange: 10.3, retention: 'N/A' },
    { month: 'Apr-25', cohortSize: 422, beforeSpend: 1716, afterSpend: 1815, change: 99, pctChange: 5.8, retention: 'N/A' },
    { month: 'May-25', cohortSize: 332, beforeSpend: 1558, afterSpend: 1521, change: -37, pctChange: -2.4, retention: 'N/A' },
    { month: 'Jun-25', cohortSize: 268, beforeSpend: 1488, afterSpend: 1570, change: 82, pctChange: 5.5, retention: 'N/A' }
  ];

  // All Products Cohort Data - From CSV  
  const allProductsCohortData = [
    { month: 'Oct-24', cohortSize: 174, beforeSpend: 2760, afterSpend: 3048, change: 288, pctChange: 10.4, daysTracked: 180 },
    { month: 'Nov-24', cohortSize: 1437, beforeSpend: 2818, afterSpend: 2955, change: 137, pctChange: 4.9, daysTracked: 180 },
    { month: 'Dec-24', cohortSize: 1459, beforeSpend: 2902, afterSpend: 3031, change: 129, pctChange: 4.4, daysTracked: 180 },
    { month: 'Jan-25', cohortSize: 1512, beforeSpend: 2766, afterSpend: 2935, change: 169, pctChange: 6.1, daysTracked: 180 },
    { month: 'Feb-25', cohortSize: 718, beforeSpend: 2948, afterSpend: 3166, change: 218, pctChange: 7.4, daysTracked: 180 },
    { month: 'Mar-25', cohortSize: 2195, beforeSpend: 2511, afterSpend: 2656, change: 145, pctChange: 5.8, daysTracked: 180 },
    { month: 'Apr-25', cohortSize: 1513, beforeSpend: 2681, afterSpend: 2779, change: 98, pctChange: 3.7, daysTracked: 180 },
    { month: 'May-25', cohortSize: 1204, beforeSpend: 2659, afterSpend: 2631, change: -29, pctChange: -1.1, daysTracked: 159 },
    { month: 'Jun-25', cohortSize: 1053, beforeSpend: 2668, afterSpend: 2628, change: -41, pctChange: -1.5, daysTracked: 131 }
  ];

  // Product Performance Data - From CSV
  const productPerformanceData = [
    { 
      product: 'DD+ Everyday',
      users: 12591,
      monthlyTxnBefore: 52.09,
      monthlyTxnAfter: 53.09,
      txnChange: 1.9,
      avgSizeBefore: 45.44,
      avgSizeAfter: 45.63,
      sizeChange: 0.4,
      spendChange: 2.3
    },
    {
      product: 'DD+ Dividend',
      users: 2446,
      monthlyTxnBefore: 51.79,
      monthlyTxnAfter: 51.37,
      txnChange: -0.8,
      avgSizeBefore: 52.72,
      avgSizeAfter: 53.30,
      sizeChange: 1.1,
      spendChange: 0.3
    },
    {
      product: 'ELITE VISA',
      users: 1277,
      monthlyTxnBefore: 20.91,
      monthlyTxnAfter: 21.79,
      txnChange: 4.2,
      avgSizeBefore: 109.97,
      avgSizeAfter: 111.97,
      sizeChange: 1.8,
      spendChange: 6.1
    },
    {
      product: 'PREFERRED VISA',
      users: 1861,
      monthlyTxnBefore: 19.55,
      monthlyTxnAfter: 19.86,
      txnChange: 1.6,
      avgSizeBefore: 118.31,
      avgSizeAfter: 117.01,
      sizeChange: -1.1,
      spendChange: 0.5
    },
    {
      product: 'EVERYDAY VISA',
      users: 1677,
      monthlyTxnBefore: 14.45,
      monthlyTxnAfter: 14.72,
      txnChange: 1.8,
      avgSizeBefore: 71.36,
      avgSizeAfter: 71.00,
      sizeChange: -0.5,
      spendChange: 1.3
    }
  ];

  // Retention Data - From CSV
  const retentionComparisonData = [
    { month: 0, scribeUp: 100, nonScribeUp: 100 },
    { month: 1, scribeUp: 98.0, nonScribeUp: 95.1 },
    { month: 2, scribeUp: 97.6, nonScribeUp: 94.5 },
    { month: 3, scribeUp: 97.3, nonScribeUp: 93.6 },
    { month: 4, scribeUp: 96.7, nonScribeUp: 93.0 },
    { month: 5, scribeUp: 96.2, nonScribeUp: 92.1 },
    { month: 6, scribeUp: 95.6, nonScribeUp: 91.5 },
    { month: 7, scribeUp: 95.0, nonScribeUp: 90.9 },
    { month: 8, scribeUp: 95.2, nonScribeUp: 90.3 },
    { month: 9, scribeUp: 93.2, nonScribeUp: 88.1 }
  ];

  // Aggregate Statistics
  const aggregateStats = {
    totalActivations: 24041,
    avgSpendLift: 5.2,
    retentionLift: 5.1,
    topPerformingProduct: 'ELITE VISA',
    avgDaysTracked: 143
  };

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900">{label}</p>
          {payload.map((entry, index) => {
            let displayValue = entry.value;
            const name = entry.name || entry.dataKey || '';
            
            if (name.toLowerCase().includes('spend') || name.toLowerCase().includes('size') || name.toLowerCase().includes('before') || name.toLowerCase().includes('after')) {
              displayValue = typeof entry.value === 'number' && entry.value > 100 ? formatCurrency(entry.value) : entry.value;
            } else if (name.toLowerCase().includes('change') || name.toLowerCase().includes('retention')) {
              displayValue = `${entry.value}%`;
            } else if (typeof entry.value === 'number' && entry.value % 1 !== 0) {
              displayValue = entry.value.toFixed(2);
            } else {
              displayValue = entry.value.toLocaleString();
            }
            
            return (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {name}: {displayValue}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Calculate summary metrics
  const calculateSummaryMetrics = () => {
    const visaAvgLift = visaCohortData.reduce((acc, d) => acc + d.pctChange, 0) / visaCohortData.length;
    const allProductsAvgLift = allProductsCohortData.reduce((acc, d) => acc + d.pctChange, 0) / allProductsCohortData.length;
    const totalCohortSize = allProductsCohortData.reduce((acc, d) => acc + d.cohortSize, 0);
    
    return {
      visaAvgLift: visaAvgLift.toFixed(1),
      allProductsAvgLift: allProductsAvgLift.toFixed(1),
      totalCohortSize,
      avgRetentionDiff: 5.1
    };
  };

  const summaryMetrics = calculateSummaryMetrics();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/analysis')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Analysis List</span>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold mb-2">ScribeUp Impact Analysis</h1>
              <p className="text-blue-100 text-lg">Comprehensive behavioral and retention analysis across all cohorts</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{aggregateStats.totalActivations.toLocaleString()}</div>
              <div className="text-blue-100">Total Activations</div>
            </div>
          </div>
          
          {/* Key Metrics Bar */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="w-5 h-5" />
                <span className="text-sm font-medium">Avg Spend Lift</span>
              </div>
              <div className="text-2xl font-bold">+{aggregateStats.avgSpendLift}%</div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Shield className="w-5 h-5" />
                <span className="text-sm font-medium">Retention Advantage</span>
              </div>
              <div className="text-2xl font-bold">+{aggregateStats.retentionLift}%</div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Award className="w-5 h-5" />
                <span className="text-sm font-medium">Top Performer</span>
              </div>
              <div className="text-2xl font-bold">{aggregateStats.topPerformingProduct}</div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">Avg Tracking</span>
              </div>
              <div className="text-2xl font-bold">{aggregateStats.avgDaysTracked} days</div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm p-2">
          <div className="flex space-x-2">
            {['overview', 'visa', 'all-products', 'retention', 'product-level'].map((view) => (
              <button
                key={view}
                onClick={() => setSelectedView(view)}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                  selectedView === view 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {view.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Content Based on Selected View */}
        {selectedView === 'overview' && (
          <div className="space-y-6">
            {/* Spend Lift Comparison */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                <BarChart3 className="w-6 h-6 text-blue-600" />
                <span>Monthly Cohort Performance Comparison</span>
              </h2>
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-blue-900">
                  Comparing spend changes across all product cohorts from Oct 2024 to Jun 2025
                </p>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={allProductsCohortData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" label={{ value: 'Monthly Spend ($)', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: 'Cohort Size', angle: 90, position: 'insideRight' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="beforeSpend" fill="#94A3B8" name="Before Activation" />
                  <Bar yAxisId="left" dataKey="afterSpend" fill="#10B981" name="After Activation" />
                  <Line yAxisId="right" type="monotone" dataKey="cohortSize" stroke="#F59E0B" strokeWidth={3} name="Cohort Size" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Monthly Spend Trend Analysis */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                <TrendingUp className="w-6 h-6 text-green-600" />
                <span>Monthly Spend Lift Trend</span>
              </h2>
              <div className="mb-4 p-4 bg-green-50 rounded-lg">
                <p className="text-green-900 text-sm">
                  Tracking spend lift percentages across all cohorts over time - showing volatility and seasonal patterns
                </p>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart 
                  data={allProductsCohortData}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `${value}%`} domain={[-2, 12]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="pctChange" 
                    stroke="#10B981" 
                    strokeWidth={3} 
                    name="Spend Lift %"
                    dot={{ fill: '#10B981', r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey={() => summaryMetrics.allProductsAvgLift}
                    stroke="#EF4444"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Average (4.8%)"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="font-semibold text-green-600">Peak: Oct-24</p>
                  <p className="text-gray-600">+10.4% lift</p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="font-semibold text-blue-600">Average</p>
                  <p className="text-gray-600">+4.8% lift</p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="font-semibold text-red-600">Low: Jun-25</p>
                  <p className="text-gray-600">-1.5% lift</p>
                </div>
              </div>
            </div>

            {/* Key Insights Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900">VISA Products</h3>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">{summaryMetrics.visaAvgLift}%</p>
                <p className="text-gray-600">Average spend increase across 3,466 VISA card activations</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Total Cohort</h3>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">{summaryMetrics.totalCohortSize.toLocaleString()}</p>
                <p className="text-gray-600">Members analyzed across 9 monthly cohorts</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Retention Lift</h3>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">+{summaryMetrics.avgRetentionDiff}%</p>
                <p className="text-gray-600">Higher retention vs non-ScribeUp members at 9 months</p>
              </div>
            </div>
          </div>
        )}

        {selectedView === 'visa' && (
          <div className="space-y-6">
            {/* VISA Cohort Analysis */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                <CreditCard className="w-6 h-6 text-blue-600" />
                <span>VISA Credit Card Cohort Analysis</span>
              </h2>
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-blue-900">
                  Monthly cohorts showing spend changes before and after ScribeUp activation for VISA credit card products
                </p>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={visaCohortData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" domain={[0, 2500]} label={{ value: 'Monthly Spend ($)', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: 'Change %', angle: 90, position: 'insideRight' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="beforeSpend" fill="#94A3B8" name="Before ScribeUp" />
                  <Bar yAxisId="left" dataKey="afterSpend" fill="#3B82F6" name="After ScribeUp" />
                  <Line yAxisId="right" type="monotone" dataKey="pctChange" stroke="#DC2626" strokeWidth={3} name="% Change" dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* VISA Performance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <ArrowUp className="w-8 h-8 text-green-600" />
                  <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded">BEST</span>
                </div>
                <p className="text-gray-600 text-sm mb-2">Highest Lift Cohort</p>
                <p className="text-2xl font-bold text-gray-900">Oct 2024</p>
                <p className="text-green-600 font-semibold">+15.1% spend increase</p>
                <p className="text-gray-500 text-sm mt-2">{formatCurrency(2067)} → {formatCurrency(2380)}</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-blue-600" />
                  <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">LARGEST</span>
                </div>
                <p className="text-gray-600 text-sm mb-2">Biggest Cohort</p>
                <p className="text-2xl font-bold text-gray-900">Mar 2025</p>
                <p className="text-blue-600 font-semibold">631 members</p>
                <p className="text-gray-500 text-sm mt-2">+10.3% spend increase</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="w-8 h-8 text-purple-600" />
                  <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-2 py-1 rounded">AVERAGE</span>
                </div>
                <p className="text-gray-600 text-sm mb-2">Overall Performance</p>
                <p className="text-2xl font-bold text-gray-900">+{summaryMetrics.visaAvgLift}%</p>
                <p className="text-purple-600 font-semibold">Avg spend lift</p>
                <p className="text-gray-500 text-sm mt-2">Across 3,466 members</p>
              </div>
            </div>

            {/* VISA Cohort Size Distribution */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Monthly Activation Volume</h2>
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 text-sm">
                  Distribution of VISA credit card ScribeUp activations by month, showing significant growth in Q1 2025
                </p>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={visaCohortData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis label={{ value: 'Number of Activations', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="cohortSize" fill="#3B82F6" name="Activations" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 text-center text-sm text-gray-600">
                Total VISA Activations: {visaCohortData.reduce((sum, d) => sum + d.cohortSize, 0).toLocaleString()}
              </div>
            </div>
          </div>
        )}

        {selectedView === 'all-products' && (
          <div className="space-y-6">
            {/* All Products Cohort Analysis */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                <Users className="w-6 h-6 text-green-600" />
                <span>All Products Cohort Performance</span>
              </h2>
              <div className="mb-4 p-4 bg-green-50 rounded-lg">
                <p className="text-green-900">
                  Combined debit and credit card products showing overall ScribeUp impact - Green bars indicate improvement, red indicates decline
                </p>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={allProductsCohortData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" domain={[2000, 3500]} label={{ value: 'Monthly Spend ($)', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 2500]} label={{ value: 'Cohort Size', angle: 90, position: 'insideRight' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="beforeSpend" fill="#94A3B8" opacity={0.7} name="Before ScribeUp" />
                  <Bar yAxisId="left" dataKey="afterSpend" name="After ScribeUp">
                    {allProductsCohortData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.pctChange > 0 ? '#10B981' : '#EF4444'} />
                    ))}
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="cohortSize" stroke="#F59E0B" strokeWidth={3} name="Cohort Size" dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
              
              {/* Gap Analysis Summary */}
              <div className="mt-6 grid grid-cols-5 gap-3">
                {allProductsCohortData.slice(0, 5).map((cohort, index) => (
                  <div key={index} className={`text-center p-3 rounded-lg border ${
                    cohort.pctChange > 5 ? 'bg-green-50 border-green-300' : 
                    cohort.pctChange > 0 ? 'bg-blue-50 border-blue-300' : 
                    'bg-red-50 border-red-300'
                  }`}>
                    <p className="text-xs font-semibold text-gray-600">{cohort.month}</p>
                    <p className={`text-lg font-bold ${
                      cohort.pctChange > 5 ? 'text-green-600' : 
                      cohort.pctChange > 0 ? 'text-blue-600' : 
                      'text-red-600'
                    }`}>
                      {cohort.pctChange > 0 ? '+' : ''}{cohort.pctChange}%
                    </p>
                    <p className="text-xs text-gray-500">{formatCurrency(cohort.change)} gap</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Spend Change Heatmap */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Monthly Spend Change Analysis</h2>
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 text-sm">
                  Visual representation of spend changes - Darker green indicates stronger positive impact, red indicates negative impact
                </p>
              </div>
              
              {/* Change Visualization */}
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={allProductsCohortData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `${value > 0 ? '+' : ''}${formatCurrency(value)}`} domain={[-100, 300]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="change" name="Spend Change ($)">
                    {allProductsCohortData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={
                        entry.change > 200 ? '#059669' :  // Dark green for high positive
                        entry.change > 100 ? '#10B981' :  // Medium green for moderate positive
                        entry.change > 0 ? '#86EFAC' :    // Light green for small positive
                        '#EF4444'                         // Red for negative
                      } />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                    <span className="text-2xl font-bold text-green-600">7 of 9</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">Positive Cohorts</p>
                  <p className="text-xs text-gray-600 mt-1">77.8% showing improvement</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <Activity className="w-6 h-6 text-blue-600" />
                    <span className="text-2xl font-bold text-blue-600">{formatCurrency(140)}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">Average Lift</p>
                  <p className="text-xs text-gray-600 mt-1">Per member per month</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <div className="flex items-center justify-between mb-2">
                    <Award className="w-6 h-6 text-yellow-600" />
                    <span className="text-2xl font-bold text-yellow-600">{formatCurrency(288)}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">Peak Impact</p>
                  <p className="text-xs text-gray-600 mt-1">Oct-24 cohort</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-4">
                {allProductsCohortData.map((cohort, index) => {
                  const intensity = cohort.pctChange > 5 ? 'high' : cohort.pctChange > 0 ? 'medium' : 'low';
                  const bgColor = intensity === 'high' ? 'bg-green-100' : intensity === 'medium' ? 'bg-blue-100' : 'bg-red-100';
                  const textColor = intensity === 'high' ? 'text-green-700' : intensity === 'medium' ? 'text-blue-700' : 'text-red-700';
                  
                  return (
                    <div key={index} className={`${bgColor} rounded-lg p-4 border`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">{cohort.month}</span>
                        <span className={`${textColor} font-bold`}>
                          {cohort.pctChange > 0 ? '+' : ''}{cohort.pctChange}%
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <div>{formatCurrency(cohort.beforeSpend)} → {formatCurrency(cohort.afterSpend)}</div>
                        <div className="text-xs mt-1">{cohort.cohortSize} members</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Product Category Performance Comparison */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Product Category Performance Summary</h2>
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 text-sm">
                  Average monthly spend lift percentage by product category - VISA credit cards lead with strongest performance
                </p>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart 
                  data={[
                    { 
                      category: 'VISA Credit Cards',
                      value: 5.7,
                      color: '#3B82F6'
                    },
                    { 
                      category: 'All Products Combined',
                      value: 4.7,
                      color: '#10B981'
                    },
                    { 
                      category: 'Non-VISA Products',
                      value: 3.2,
                      color: '#F59E0B'
                    }
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis tickFormatter={(value) => `${value}%`} domain={[0, 7]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Average Spend Lift %">
                    {[
                      { color: '#3B82F6' },
                      { color: '#10B981' },
                      { color: '#F59E0B' }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                    <span className="text-2xl font-bold text-blue-600">5.7%</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">VISA Credit Cards</p>
                  <p className="text-xs text-gray-600 mt-1">3,466 members</p>
                  <p className="text-xs text-gray-600">$1,795 avg monthly spend</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <Users className="w-6 h-6 text-green-600" />
                    <span className="text-2xl font-bold text-green-600">4.7%</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">All Products</p>
                  <p className="text-xs text-gray-600 mt-1">10,065 members</p>
                  <p className="text-xs text-gray-600">$2,831 avg monthly spend</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <div className="flex items-center justify-between mb-2">
                    <Activity className="w-6 h-6 text-yellow-600" />
                    <span className="text-2xl font-bold text-yellow-600">3.2%</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">Non-VISA Products</p>
                  <p className="text-xs text-gray-600 mt-1">9,344 members</p>
                  <p className="text-xs text-gray-600">$2,341 avg monthly spend</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedView === 'retention' && (
          <div className="space-y-6">
            {/* Retention Curve Comparison */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                <Shield className="w-6 h-6 text-purple-600" />
                <span>Retention Comparison: ScribeUp vs Non-ScribeUp</span>
              </h2>
              <div className="mb-4 p-4 bg-purple-50 rounded-lg">
                <p className="text-purple-900">
                  9-month retention tracking showing ScribeUp's impact on member longevity (Jan 2025 cohort)
                </p>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={retentionComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" label={{ value: 'Months Since Activation', position: 'insideBottom', offset: -5 }} />
                  <YAxis domain={[85, 102]} tickFormatter={(value) => `${value}%`} label={{ value: 'Retention Rate %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="scribeUp" stroke="#10B981" strokeWidth={3} name="ScribeUp Members" dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="nonScribeUp" stroke="#EF4444" strokeWidth={3} name="Non-ScribeUp Members" dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Retention Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                <UserCheck className="w-8 h-8 text-green-600 mb-3" />
                <p className="text-gray-600 text-sm mb-2">ScribeUp 9-Month</p>
                <p className="text-3xl font-bold text-gray-900">93.2%</p>
                <p className="text-green-600 text-sm mt-2">Retention Rate</p>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6">
                <Users className="w-8 h-8 text-red-600 mb-3" />
                <p className="text-gray-600 text-sm mb-2">Non-ScribeUp 9-Month</p>
                <p className="text-3xl font-bold text-gray-900">88.1%</p>
                <p className="text-red-600 text-sm mt-2">Retention Rate</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                <TrendingUp className="w-8 h-8 text-blue-600 mb-3" />
                <p className="text-gray-600 text-sm mb-2">Retention Advantage</p>
                <p className="text-3xl font-bold text-gray-900">+5.1%</p>
                <p className="text-blue-600 text-sm mt-2">Percentage Points</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
                <Award className="w-8 h-8 text-purple-600 mb-3" />
                <p className="text-gray-600 text-sm mb-2">Members Saved</p>
                <p className="text-3xl font-bold text-gray-900">~250</p>
                <p className="text-purple-600 text-sm mt-2">Per 4,881 cohort</p>
              </div>
            </div>

            {/* Retention by Month Cohort */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Retention Curves by Activation Cohort</h2>
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 text-sm">
                  Tracking retention rates over time for the 5 longest-tracked ScribeUp cohorts
                </p>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart 
                  data={[
                    { month: 0, 'Oct-24': 100, 'Nov-24': 100, 'Dec-24': 100, 'Jan-25': 100, 'Feb-25': 100 },
                    { month: 1, 'Oct-24': 97.4, 'Nov-24': 97.9, 'Dec-24': 98.0, 'Jan-25': 98.0, 'Feb-25': 97.4 },
                    { month: 2, 'Oct-24': 95.2, 'Nov-24': 96.9, 'Dec-24': 96.3, 'Jan-25': 97.6, 'Feb-25': 96.9 },
                    { month: 3, 'Oct-24': 95.8, 'Nov-24': 97.0, 'Dec-24': 97.2, 'Jan-25': 97.3, 'Feb-25': 96.6 },
                    { month: 4, 'Oct-24': 95.8, 'Nov-24': 96.5, 'Dec-24': 96.6, 'Jan-25': 96.7, 'Feb-25': 95.2 },
                    { month: 5, 'Oct-24': 95.8, 'Nov-24': 95.9, 'Dec-24': 96.1, 'Jan-25': 96.2, 'Feb-25': 94.7 },
                    { month: 6, 'Oct-24': 94.2, 'Nov-24': 95.6, 'Dec-24': 95.3, 'Jan-25': 95.6, 'Feb-25': 94.9 },
                    { month: 7, 'Oct-24': 94.7, 'Nov-24': 95.3, 'Dec-24': 95.3, 'Jan-25': 95.0, 'Feb-25': 94.7 },
                    { month: 8, 'Oct-24': 95.2, 'Nov-24': 95.0, 'Dec-24': 94.3, 'Jan-25': 95.2, 'Feb-25': 93.6 },
                    { month: 9, 'Oct-24': 95.8, 'Nov-24': 94.3, 'Dec-24': 94.3, 'Jan-25': 93.2, 'Feb-25': null },
                    { month: 10, 'Oct-24': 94.7, 'Nov-24': 94.0, 'Dec-24': 93.0, 'Jan-25': null, 'Feb-25': null },
                    { month: 11, 'Oct-24': 93.7, 'Nov-24': 92.5, 'Dec-24': null, 'Jan-25': null, 'Feb-25': null },
                    { month: 12, 'Oct-24': 93.1, 'Nov-24': null, 'Dec-24': null, 'Jan-25': null, 'Feb-25': null }
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" label={{ value: 'Months Since Activation', position: 'insideBottom', offset: -5 }} />
                  <YAxis domain={[90, 102]} tickFormatter={(value) => `${value}%`} label={{ value: 'Retention Rate %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="Oct-24" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                  <Line type="monotone" dataKey="Nov-24" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                  <Line type="monotone" dataKey="Dec-24" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                  <Line type="monotone" dataKey="Jan-25" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                  <Line type="monotone" dataKey="Feb-25" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-4 grid grid-cols-5 gap-4 text-sm">
                <div className="text-center">
                  <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-2"></span>
                  <span className="text-gray-600">Oct-24: 12 months</span>
                </div>
                <div className="text-center">
                  <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                  <span className="text-gray-600">Nov-24: 11 months</span>
                </div>
                <div className="text-center">
                  <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                  <span className="text-gray-600">Dec-24: 10 months</span>
                </div>
                <div className="text-center">
                  <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 mr-2"></span>
                  <span className="text-gray-600">Jan-25: 9 months</span>
                </div>
                <div className="text-center">
                  <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                  <span className="text-gray-600">Feb-25: 8 months</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedView === 'product-level' && (
          <div className="space-y-6">
            {/* Product Performance Table */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                <BarChart3 className="w-6 h-6 text-indigo-600" />
                <span>Product-Level Performance Analysis</span>
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Txn Change</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size Change</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spend Change</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {productPerformanceData.map((product, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {product.product}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {product.users.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            product.txnChange > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {product.txnChange > 0 ? '+' : ''}{product.txnChange}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            product.sizeChange > 0 ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {product.sizeChange > 0 ? '+' : ''}{product.sizeChange}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            product.spendChange > 0 ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {product.spendChange > 0 ? '+' : ''}{product.spendChange}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Product Performance Metrics */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Product Performance Impact</h2>
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 text-sm">
                  Overall spend change percentage by product after ScribeUp activation
                </p>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart 
                  data={[
                    { name: 'DD+ Everyday', value: 2.3, users: 12591 },
                    { name: 'DD+ Dividend', value: 0.3, users: 2446 },
                    { name: 'ELITE VISA', value: 6.1, users: 1277 },
                    { name: 'PREFERRED', value: 0.5, users: 1861 },
                    { name: 'EVERYDAY', value: 1.3, users: 1677 }
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-20} textAnchor="end" height={80} />
                  <YAxis yAxisId="left" label={{ value: 'Spend Change %', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: 'Number of Users', angle: 90, position: 'insideRight' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="value" name="Spend Change %">
                    <Cell fill="#10B981" />
                    <Cell fill="#94A3B8" />
                    <Cell fill="#F59E0B" />
                    <Cell fill="#3B82F6" />
                    <Cell fill="#8B5CF6" />
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="users" stroke="#DC2626" strokeWidth={3} name="User Count" dot={{ r: 5 }} />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-center space-x-2 mb-1">
                    <div className="w-3 h-3 bg-blue-500 rounded"></div>
                    <span className="text-sm font-medium text-gray-700">Transaction Change</span>
                  </div>
                  <p className="text-xs text-gray-600">Monthly transaction frequency</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-center space-x-2 mb-1">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span className="text-sm font-medium text-gray-700">Size Change</span>
                  </div>
                  <p className="text-xs text-gray-600">Average transaction value</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center justify-center space-x-2 mb-1">
                    <div className="w-3 h-3 bg-purple-500 rounded"></div>
                    <span className="text-sm font-medium text-gray-700">Spend Change</span>
                  </div>
                  <p className="text-xs text-gray-600">Total monthly spending</p>
                </div>
              </div>
            </div>

            {/* Product Performance Ranking */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Product Performance Ranking</h2>
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 text-sm">
                  Detailed performance metrics ranked by total spend impact
                </p>
              </div>
              <div className="space-y-4">
                {[
                  { rank: 1, product: 'ELITE VISA', spend: 6.1, txn: 4.2, size: 1.8, users: 1277, color: 'bg-yellow-100 border-yellow-300' },
                  { rank: 2, product: 'DD+ Everyday', spend: 2.3, txn: 1.9, size: 0.4, users: 12591, color: 'bg-gray-50 border-gray-200' },
                  { rank: 3, product: 'EVERYDAY VISA', spend: 1.3, txn: 1.8, size: -0.5, users: 1677, color: 'bg-orange-50 border-orange-200' },
                  { rank: 4, product: 'PREFERRED VISA', spend: 0.5, txn: 1.6, size: -1.1, users: 1861, color: 'bg-white border-gray-200' },
                  { rank: 5, product: 'DD+ Dividend', spend: 0.3, txn: -0.8, size: 1.1, users: 2446, color: 'bg-white border-gray-200' }
                ].map((item) => (
                  <div key={item.rank} className={`${item.color} border-2 rounded-lg p-4 transition-all hover:shadow-md`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                          item.rank === 1 ? 'bg-yellow-400 text-white' : 
                          item.rank === 2 ? 'bg-gray-400 text-white' : 
                          item.rank === 3 ? 'bg-orange-400 text-white' : 
                          'bg-gray-200 text-gray-700'
                        }`}>
                          {item.rank}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{item.product}</p>
                          <p className="text-sm text-gray-600">{item.users.toLocaleString()} users</p>
                        </div>
                      </div>
                      <div className="flex space-x-6">
                        <div className="text-center">
                          <p className={`text-lg font-bold ${item.spend > 2 ? 'text-green-600' : item.spend > 0 ? 'text-blue-600' : 'text-gray-600'}`}>
                            {item.spend > 0 ? '+' : ''}{item.spend}%
                          </p>
                          <p className="text-xs text-gray-500">Spend</p>
                        </div>
                        <div className="text-center">
                          <p className={`text-lg font-semibold ${item.txn > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                            {item.txn > 0 ? '+' : ''}{item.txn}%
                          </p>
                          <p className="text-xs text-gray-500">Transactions</p>
                        </div>
                        <div className="text-center">
                          <p className={`text-lg font-semibold ${item.size > 0 ? 'text-purple-600' : 'text-orange-600'}`}>
                            {item.size > 0 ? '+' : ''}{item.size}%
                          </p>
                          <p className="text-xs text-gray-500">Avg Size</p>
                        </div>
                      </div>
                    </div>
                    {item.rank === 1 && (
                      <div className="mt-3 pt-3 border-t border-yellow-300">
                        <div className="flex items-center space-x-2 text-yellow-700">
                          <Award className="w-4 h-4" />
                          <span className="text-sm font-medium">Top Performer - Highest spend increase across all products</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Top Performers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Award className="w-8 h-8 text-yellow-600" />
                  <span className="text-xs font-semibold text-yellow-600 bg-yellow-200 px-2 py-1 rounded">TOP SPEND</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">ELITE VISA</p>
                <p className="text-yellow-600 font-semibold">+6.1% spend increase</p>
                <p className="text-gray-600 text-sm mt-2">1,277 users</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-blue-600" />
                  <span className="text-xs font-semibold text-blue-600 bg-blue-200 px-2 py-1 rounded">MOST USERS</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">DD+ Everyday</p>
                <p className="text-blue-600 font-semibold">12,591 users</p>
                <p className="text-gray-600 text-sm mt-2">+2.3% spend increase</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Activity className="w-8 h-8 text-green-600" />
                  <span className="text-xs font-semibold text-green-600 bg-green-200 px-2 py-1 rounded">TOP TXN</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">ELITE VISA</p>
                <p className="text-green-600 font-semibold">+4.2% txn increase</p>
                <p className="text-gray-600 text-sm mt-2">20.91 → 21.79 monthly</p>
              </div>
            </div>
          </div>
        )}

        {/* Executive Summary */}
        <div className="bg-gradient-to-r from-gray-900 to-blue-900 rounded-2xl p-8 text-white shadow-xl">
          <h2 className="text-3xl font-bold mb-6">Executive Summary</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center space-x-2">
                <Target className="w-6 h-6" />
                <span>Key Achievements</span>
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span><strong>24,041 total activations</strong> across 9 monthly cohorts with consistent growth</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span><strong>5.2% average spend increase</strong> post-activation across all products</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span><strong>5.1% higher retention</strong> at 9 months vs non-ScribeUp members</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span><strong>VISA products outperform</strong> with {summaryMetrics.visaAvgLift}% average lift</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center space-x-2">
                <AlertCircle className="w-6 h-6" />
                <span>Strategic Insights</span>
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span><strong>Premium products respond best:</strong> ELITE VISA leads with 6.1% spend increase</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span><strong>Retention compounds value:</strong> ~250 additional members retained per 5K cohort</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span><strong>Behavioral shift pattern:</strong> Initial optimization phase followed by sustained growth</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span><strong>Scale opportunity:</strong> 12,591 DD+ Everyday users show 2.3% lift potential</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-8 p-6 bg-white/10 backdrop-blur rounded-xl">
            <p className="text-blue-100 leading-relaxed">
              <strong className="text-white">Bottom Line:</strong> ScribeUp delivers measurable impact across both spend behavior 
              (+5.2% average) and member retention (+5.1% at 9 months). With 24,041 activations showing consistent positive results, 
              the program demonstrates strong product-market fit and scalability potential. Premium products show highest responsiveness, 
              suggesting opportunities for targeted expansion strategies.
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to explore portfolio performance?</h3>
              <p className="text-gray-600">
                View detailed investment returns and portfolio metrics for the ScribeUp initiative.
              </p>
            </div>
            <button
              onClick={() => onSectionChange?.('portfolio')}
              className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg"
            >
              View Portfolio Details
              <ChevronRight className="inline-block ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScribeUpAnalysisV2;